import { CheckCircleTwoTone, DownOutlined } from '@ant-design/icons';
import { Button, Dropdown, Tooltip } from 'antd';
import { type ItemType } from 'antd/es/menu/interface';
import { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { useTheme } from 'styled-components';

import { DataTableContext } from 'components/DataTable';
import { removeSearchParam } from 'components/DataTable/Views/searchParams';
import { StyledTitle } from 'components/DataTable/Views/Views.style';
import {
  type AtlasGqlTableView,
  useConfiguredMakeModelCombinationsQuery,
} from 'types/atlas-graphql';

import { getRelevantDefectPatterns } from './getRelevantDefectPatterns';
import { useDefectPatternViews, useDefectPatternViewsLazy } from './useDefectPatternViews';

// The Sites or Assets the table is scoped to. Only Defect Patterns for the Make/Models
// configured there are offered.
export type DefectPatternsScope = {
  siteIds?: string[];
  assetIds?: string[];
};

type DefectPatternsProps = DefectPatternsScope & {
  tableId?: string;
  applyViewToTable: (tableViewToApply?: AtlasGqlTableView) => void;
  onSelect?: () => void;
};

export const DefectPatterns: React.FC<DefectPatternsProps> = ({
  siteIds,
  assetIds,
  tableId,
  applyViewToTable,
  onSelect,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { tableView } = useContext(DataTableContext);

  const location = useLocation();
  const history = useHistory();
  // The effect re-runs on every render while a lookup is in flight, so it skips the id it's
  // already looking up. Cleared once the param is gone, so a later link to the same id applies.
  const pendingViewIdRef = useRef<string>();

  const { views, loading } = useDefectPatternViews();
  const loadViews = useDefectPatternViewsLazy();

  // Dashboard Defect Pattern links open this table with `?view=<id>`. Defect Pattern views are
  // global, so `tableView(id)` can't resolve them. The lookup is async so the view lands after
  // the table restores its stored view on mount, and the param is stripped either way.
  useEffect(() => {
    if (!tableId) {
      return;
    }
    const viewId = new URLSearchParams(location.search).get('view');
    if (!viewId) {
      // Navigating away from the link drops its lookup, so the view doesn't land after the user left
      pendingViewIdRef.current = undefined;
      return;
    }
    if (viewId === pendingViewIdRef.current) {
      return;
    }

    pendingViewIdRef.current = viewId;
    // A later link replaces this lookup, so only the latest one applies its view or touches the URL.
    const isLatest = () => pendingViewIdRef.current === viewId;
    loadViews()
      .then(loadedViews => {
        const matchedView = loadedViews.find(v => v?.id === viewId);
        if (matchedView && isLatest()) {
          applyViewToTable({ ...matchedView, tableName: tableId });
        }
      })
      // Not retried: the effect re-runs on every render, so a retry would repeat a failing query.
      // The table stays unfiltered, as it does for an id that matches no Defect Pattern.
      .catch(() => {})
      .finally(() => {
        if (isLatest()) {
          removeSearchParam(history, 'view');
          pendingViewIdRef.current = undefined;
        }
      });
  }, [applyViewToTable, history, loadViews, location.search, tableId]);

  // An Asset scope is narrower than its Site, so it wins when both are set.
  const { data: turbineMakeModelData } = useConfiguredMakeModelCombinationsQuery({
    variables: {
      input: {
        assetTypeName: 'Turbine',
        ...(assetIds?.length ? { assetIds } : siteIds?.length ? { siteIds } : {}),
      },
    },
  });
  const { data: bladeMakeModelData } = useConfiguredMakeModelCombinationsQuery({
    variables: {
      input: {
        assetTypeName: 'Blade',
        ...(assetIds?.length ? { ancestorAssetIds: assetIds } : siteIds?.length ? { siteIds } : {}),
      },
    },
  });

  const relevantViews = getRelevantDefectPatterns(
    views,
    turbineMakeModelData?.configuredMakeModelCombinations.existingValues ?? [],
    bladeMakeModelData?.configuredMakeModelCombinations.existingValues ?? []
  );

  const items: ItemType[] = relevantViews.map(view => ({
    key: view.id,
    label: (
      <StyledTitle>
        {view.id === tableView?.id && <CheckCircleTwoTone twoToneColor={theme.green} />}
        <span className="defect-pattern-name">{view.name}</span>
      </StyledTitle>
    ),
    onClick: () => {
      if (tableId) {
        applyViewToTable({ ...view, tableName: tableId });
      }
      if (onSelect) {
        onSelect();
      }
    },
  }));

  const tooltipMessage = !!items.length
    ? t(
        'damage.defectPatternsTooltip',
        'Data indicates the Damages within these regions may see higher risks of rapid Damage Propagation over time.'
      )
    : t(
        'damage.defectPatternsEmptyTooltip',
        'There are no known Defect Patterns that match your Damage data. Filling in Blade Make/Model on your Assets may make some available.'
      );

  return (
    <Dropdown
      menu={{ items, style: { maxHeight: 350, overflowY: 'scroll' } }}
      trigger={['click']}
      placement="bottomLeft"
    >
      <Tooltip title={tooltipMessage}>
        <Button data-testid="defect-patterns-dropdown" disabled={!items.length} loading={loading}>
          {t('damage.defectPatterns', 'Defect Patterns')} <DownOutlined />
        </Button>
      </Tooltip>
    </Dropdown>
  );
};
